import { readJsonObject, STORAGE_KEYS, storage, writeJson } from './store';

/** Query keys captured, in this order (campaign parameters, then click identifiers). */
export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
] as const;

export const ATTRIBUTION_WINDOW_MS = 90 * 86_400_000;
const VALUE_LIMIT = 200;
const KEY_LIMIT = 40;
const CART_ATTRIBUTE_LIMIT = 25;

export type Touch = Readonly<Record<string, string>>;

/** `m618_attrib_v1` — first touch is never overwritten, last touch always is. */
export interface AttributionStore {
  readonly first?: Touch;
  readonly first_ts?: number;
  readonly first_lp?: string;
  readonly last?: Touch;
  readonly last_ts?: number;
  readonly last_lp?: string;
}

export interface CartAttributeInput {
  readonly key: string;
  readonly value: string;
}

export interface AttributionContext {
  readonly search: string;
  readonly pathname: string;
  readonly referrer: string;
  readonly now: number;
}

function isTouch(value: unknown): value is Touch {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The stored attribution, or `{}` (M618.attrib()). */
export function readAttribution(): AttributionStore {
  const raw = readJsonObject(STORAGE_KEYS.attribution);
  if (raw === null) return {};
  const store: {
    first?: Touch;
    first_ts?: number;
    first_lp?: string;
    last?: Touch;
    last_ts?: number;
    last_lp?: string;
  } = {};
  if (isTouch(raw.first)) store.first = raw.first;
  if (typeof raw.first_ts === 'number') store.first_ts = raw.first_ts;
  if (typeof raw.first_lp === 'string') store.first_lp = raw.first_lp;
  if (isTouch(raw.last)) store.last = raw.last;
  if (typeof raw.last_ts === 'number') store.last_ts = raw.last_ts;
  if (typeof raw.last_lp === 'string') store.last_lp = raw.last_lp;
  return store;
}

function readQuery(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  const query = new URLSearchParams(search);
  for (const key of ATTRIBUTION_PARAMS) {
    const value = query.get(key);
    if (value) out[key] = value.slice(0, VALUE_LIMIT);
  }
  return out;
}

/**
 * Runs once per document at boot, regardless of consent (attribution is first-party and
 * leaves the origin only as a cart attribute on an order the visitor places).
 */
export function captureAttribution(context: AttributionContext): AttributionStore {
  let store: AttributionStore = readAttribution();
  const fresh = readQuery(context.search);
  if (store.last_ts !== undefined && store.last_ts !== 0 && context.now - store.last_ts > ATTRIBUTION_WINDOW_MS) {
    store = {};
  }
  if (Object.keys(fresh).length > 0) {
    store =
      store.first === undefined
        ? { first: fresh, first_ts: context.now, first_lp: context.pathname, last: fresh }
        : { ...store, last: fresh };
    store = { ...store, last_ts: context.now, last_lp: context.pathname };
    writeJson(STORAGE_KEYS.attribution, store);
  } else if (store.first === undefined) {
    const referrer = context.referrer;
    const touch: Record<string, string> = referrer
      ? { utm_source: 'referral', utm_medium: 'referral', referrer: referrer.slice(0, VALUE_LIMIT) }
      : { utm_source: 'direct', utm_medium: 'none' };
    store = {
      ...store,
      first: touch,
      first_ts: context.now,
      first_lp: context.pathname,
      last: touch,
      last_ts: context.now,
      last_lp: context.pathname,
    };
    writeJson(STORAGE_KEYS.attribution, store);
  }
  return store;
}

/** First-party client id `m618_cid`, created on first use: 'c' + base36 time + 8 base36 chars. */
export function clientId(now: number = Date.now(), random: () => number = Math.random): string {
  const existing = storage.get(STORAGE_KEYS.clientId);
  if (existing) return existing;
  const id = 'c' + now.toString(36) + random().toString(36).slice(2, 10);
  storage.set(STORAGE_KEYS.clientId, id);
  return id;
}

/**
 * Order attributes (sent to the till with a checkout; formerly Shopify cart attributes): first_<k>…, last_<k>…, first_landing, first_seen, m618_client_id.
 * Keys ≤ 40 chars, values ≤ 200, empty values skipped, at most 25 entries.
 */
export function cartAttributes(): CartAttributeInput[] {
  const store = readAttribution();
  const out: CartAttributeInput[] = [];
  const push = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    out.push({ key: key.slice(0, KEY_LIMIT), value: String(value).slice(0, VALUE_LIMIT) });
  };
  for (const [key, value] of Object.entries(store.first ?? {})) push(`first_${key}`, value);
  for (const [key, value] of Object.entries(store.last ?? {})) push(`last_${key}`, value);
  if (store.first_lp) push('first_landing', store.first_lp);
  if (store.first_ts && Number.isFinite(store.first_ts)) push('first_seen', new Date(store.first_ts).toISOString());
  push('m618_client_id', clientId());
  return out.slice(0, CART_ATTRIBUTE_LIMIT);
}
